import React from 'react';
import './HowToReadThisReport.scss';

const SCORE_BANDS = [
  { range: '1.0–3.9', label: 'Low tendency' },
  { range: '4.0–6.4', label: 'Moderate tendency' },
  { range: '6.5–10.0', label: 'High tendency' },
];

const INFERENCES = [
  {
    title: 'Communication style',
    description: 'Predicts how the person typically interacts at work: reserved vs outgoing, energy in social settings, confidence in groups, and willingness to speak up.',
  },
  {
    title: 'Stress style',
    description: 'Predicts how the person typically responds under pressure: calm vs reactive, independent vs reassurance-seeking, and sensitivity to perceived risk.',
  },
  {
    title: 'Work style',
    description: 'Predicts how the person typically executes: discipline and follow-through, standards and detail focus, decision caution vs speed, and preference for order.',
  },
  {
    title: 'Collaboration style',
    description: 'Predicts how the person typically handles friction: kindness vs bluntness, flexibility vs rigidity, patience under delays, and how quickly they reset after conflict.',
  },
  {
    title: 'Change style',
    description: 'Predicts how the person typically engages with ideas and change: pragmatic vs experimental, curiosity, and comfort challenging norms.',
  },
  {
    title: 'Incentive sensitivity',
    description: 'Predicts what typically motivates or triggers tension: importance of recognition/rewards/status, comfort with self-promotion vs modesty, and fairness sensitivity around credit and outcomes.',
  },
];

const CANNOT_INFER = [
  'Role capability or job performance by itself',
  'Domain knowledge, technical skill, or cognitive ability',
  'Ethics in real-world situations without corroboration',
  'Culture fit in a specific team without context',
  'How the person will behave in every situation (this is about tendencies, not guarantees)',
];

const HowToReadThisReport = () => (
  <div className="htrt">
    <div className="htrt-section">
      <h3 className="htrt-heading">How to read these scores</h3>
      <p className="htrt-text">
        This report describes behavioural tendencies. Scores are not "good" or "bad", and they do
        not measure skill, intelligence, or job performance on their own. Higher and lower scores
        indicate different tendencies, each with predictable tradeoffs in how a person may behave at
        work.
      </p>
      <p className="htrt-text">
        Each competency and sub-competency is defined inside the report; interpret scores only
        using those definitions. Use sub-competency scores to understand what is driving the overall
        competency score.
      </p>
    </div>

    <div className="htrt-section">
      <h3 className="htrt-heading">Score bands</h3>
      <ul className="htrt-band-list">
        {SCORE_BANDS.map(({ range, label }) => (
          <li key={range} className="htrt-band-item">
            <span className="htrt-band-range">{range}:</span> {label}
          </li>
        ))}
      </ul>
    </div>

    <div className="htrt-section">
      <h3 className="htrt-heading">What you can infer from this report</h3>
      <ol className="htrt-infer-list">
        {INFERENCES.map(({ title, description }) => (
          <li key={title} className="htrt-infer-item">
            <strong>{title}</strong>
            <p className="htrt-text">{description}</p>
          </li>
        ))}
      </ol>
    </div>

    <div className="htrt-section">
      <h3 className="htrt-heading">What you cannot infer from this report</h3>
      <ul className="htrt-cannot-list">
        {CANNOT_INFER.map((item) => (
          <li key={item} className="htrt-cannot-item">{item}</li>
        ))}
      </ul>
    </div>
  </div>
);

export default HowToReadThisReport;
